import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/nuxt';
import { notifyError } from '~/utils/notify';

/**
 * The terminal Pinia store.
 */
export const useTerminalStore = defineStore('terminal', {
  state: () => ({
    /**
     * The generated order.
     */
    order: null as Order | null,

    /**
     * The payment reference to apply to the order.
     */
    paymentReference: '' as string,

    /**
     * The current redeem location.
     */
    redeemLocation: null as RedeemLocation | null,

    /**
     * The current sales channel.
     */
    salesChannel: null as SalesChannel | null,

    /**
     * The basket.
     */
    basket: null as Basket | null,

    /**
     * The checkout basket.
     */
    checkoutBasket: null as Basket | null,

    /**
     * The buyer details captured at the point of taking an offline payment.
     * All fields are optional; populated by the offline payment dialog.
     */
    buyer: {
      email: '',
      name: '',
      address: {
        line_1: '',
        line_2: '',
        city: '',
        country: '',
        postcode: '',
      },
    } as BuyerDetails,

    /**
     * The transaction created by sending a card payment link. Populated by
     * sendPaymentLink and consumed by the waiting page (status + destination
     * email). Each "send link" replaces it with a fresh attempt.
     */
    paymentLinkTransaction: null as Transaction | null,

    /**
     * The product.
     */
    product: null as BasketProduct | null,

    /**
     * The redeem locations.
     */
    redeemLocations: [] as RedeemLocation[],

    /**
     * The products.
     */
    products: [] as Product[],

    /**
     * The discounts.
     */
    discounts: [] as BasketDiscount[],

    /**
     * The discount code error.
     */
    codeError: null as string | null,

    /**
     * The promotion refused by the most recent basket validate, with the
     * pairing data its message was built from. Surfaced as state (instead of
     * thrown) so the summary page can prompt for the unit that would free one.
     */
    promotionRefusal: null as PromotionRefusal | null,

    /**
     * The staff discount amount.
     */
    staffDiscountAmount: null as number | null,

    /**
     * The redeemables to be redeemed.
     */
    redeemables: [] as Redeemable[],

    /**
     * The number of redeemables that have been redeemed.
     */
    redemptionCount: null as number | null,

    /**
     * The reference of the voucher whose redeemables have been redeemed.
     */
    voucherReference: null as string | null,

    /**
     * The "back to" button option.
     */
    backToButton: null as { destination: string; text: string } | null,

    summaryPageProcessingDiscount: false as boolean,
    /**
     * The selected cost centre ID for staff discounts.
     */
    staffDiscountCostCentreId: null as string | null,
    /**
     * The free-text reason for the staff discount.
     */
    staffDiscountReason: null as string | null,

    /**
     * Stock-related error message from the most recent basket validate call's
     * 422 response. Surfaced as state (instead of thrown) so the summary page
     * can keep rendering and offer the override checkbox.
     */
    basketStockError: null as string | null,

    /**
     * Error message from the most recent basket sync's 422 response.
     */
    basketSyncError: null as string | null,
  }),

  getters: {
    /**
     * The payment gateway configured for the current sales channel, used for
     * card payment links. Mirrors the storefront's gateway derivation: prefer
     * Stripe, fall back to Fanbasis, otherwise null (no card payments offered).
     */
    gateway: (state): 'stripe' | 'fanbasis' | null => {
      const channel = state.salesChannel as SalesChannel | null;

      if (!channel) {
        return null;
      }

      if (channel.stripe_account_id) {
        return 'stripe';
      }

      if (channel.fanbasis_account_id) {
        return 'fanbasis';
      }

      return null;
    },

    /**
     * The total number of redeemables.
     */
    redeemablesCount: (state): number => state.redeemables.length,

    /**
     * The valid redeemables.
     */
    validRedeemables: (state): Redeemable[] =>
      state.redeemables.filter(
        (redeemable: Redeemable): boolean => redeemable.invalid_reason === null,
      ),

    basketDisplayPrice: (state): number => {
      if (state.basket === null) {
        return 0;
      }

      // Return the sum of the basket's products' server-set display prices
      return (state.basket as Basket).data.products.reduce(
        (total: number, product: BasketItem): number =>
          total + (product.display_price ?? 0),
        0,
      );
    },
    collectEnabled: (state): boolean => {
      if (!(state.salesChannel as SalesChannel).fulfilment_is_enabled) {
        return false;
      }

      // Active packaging options with collection
      const activePackagingWithCollection: PackagingOption[] = (
        state.salesChannel as SalesChannel
      ).packaging_options.filter(
        (packagingOption: PackagingOption) =>
          packagingOption.collection_is_active && packagingOption.is_active,
      );

      return activePackagingWithCollection.length > 0;
    },
    postEnabled: (state): boolean => {
      if (!(state.salesChannel as SalesChannel).fulfilment_is_enabled) {
        return false;
      }

      // Active delivery methods
      const activeDeliveryMethods: DeliveryMethod[] = (
        state.salesChannel as SalesChannel
      ).delivery_methods.filter(
        (deliveryMethod: DeliveryMethod) => deliveryMethod.is_active,
      );

      if (activeDeliveryMethods.length === 0) {
        return false;
      }

      // Active packaging options with active delivery methods
      const activePackagingWithDeliveryMethods: PackagingOption[] = (
        state.salesChannel as SalesChannel
      ).packaging_options.filter(
        (packagingOption: PackagingOption) =>
          packagingOption.is_active &&
          packagingOption.delivery_methods.filter(
            (deliveryMethod: DeliveryMethod) => deliveryMethod.is_active,
          ).length > 0,
      );

      return activePackagingWithDeliveryMethods.length > 0;
    },
  },

  actions: {
    initialiseBasket(): void {
      this.basket = {
        store_eni: (this.salesChannel as SalesChannel).eni,
        customer_id: uuidv4(),
        data: {
          products: [],
        },
      };
    },

    initialiseBasketProduct(product: Product): void {
      this.product = {
        ...product,
        basketData: {
          id: product.id,
          eni: product.eni,
          image_url: product.custom_fields.image_urls[0],
          from_name: '',
          recipient_name: '',
          message: '',
          addons: [],
        },
      };
    },

    async addBasketProduct(product: BasketProduct): Promise<boolean> {
      // If the basket is not set
      if (this.basket === null) {
        // Initialise the basket
        this.initialiseBasket();
      }

      const basket: Basket = this.basket as Basket;

      // Push the product onto the basket
      const index: number =
        basket.data.products.push(product.basketData as BasketItem) - 1;

      const synced = await this.syncBasket();

      if (!synced) {
        // Remove the rejected product from the basket
        basket.data.products.splice(index, 1);

        notifyError(
          'Product not added',
          this.basketSyncError ?? 'The product could not be added.',
        );
      }

      return synced;
    },

    async updateBasketProduct(product: BasketProduct): Promise<boolean> {
      const basket: Basket = this.basket as Basket;
      const index: number = product.index as number;
      const previous: BasketItem = basket.data.products[index] as BasketItem;

      // Update the basket's products
      basket.data.products[index] = product.basketData;

      const synced = await this.syncBasket();

      if (!synced) {
        // Restore the previous product on the rejected change
        basket.data.products[index] = previous;

        notifyError(
          'Product not updated',
          this.basketSyncError ?? 'The product could not be updated.',
        );
      }

      return synced;
    },

    async removeBasketProduct(
      product: BasketProduct,
      type: 'test' | 'live',
    ): Promise<void> {
      const basket: Basket = this.basket as Basket;

      // And remove the product from the basket's products
      basket.data.products = basket.data.products
        .slice(0, product.index as number)
        .concat(basket.data.products.slice((product.index as number) + 1));

      // If no products are left in the basket
      if (basket.data.products.length === 0) {
        // Hard reset
        window.location.href = `/terminal/sell/${type}/${(this.salesChannel as SalesChannel).id}`;

        return;
      }

      // Sync the removal with the server
      await this.syncBasket();
    },

    /**
     * Sync the basket with the server as a draft, returning whether it was
     * accepted. Callers await the sync and disable their controls while it
     * runs, so syncs never overlap.
     */
    async syncBasket(retryWithoutPromotion = false): Promise<boolean> {
      if (this.basket === null) {
        return true;
      }

      // Use a sanitized copy of the basket, flagged as a draft
      const body: Basket & { draft?: boolean } = this.sanitizeBasket(
        JSON.parse(JSON.stringify(this.basket)) as Basket,
      );

      body.draft = true;

      // The promotion the request carries owns any refusal it comes back with
      const sentPromotion = body.data.promotion;

      try {
        const response = await apiPost('/terminal/basket/validate', body);

        this.applyHydratedBasket(response.basket);

        this.basketSyncError = null;

        // A synced basket change drops the refusal the previous basket carried
        if (!retryWithoutPromotion) {
          this.promotionRefusal = null;
        }

        return true;
      } catch (error: any) {
        // A refused promotion is the promotion's failure, not the change's
        if (this.promotionWasRefused(error)) {
          const messages = error.data.errors.promotion;

          this.promotionRefusal = {
            message: Array.isArray(messages) ? messages.join(', ') : messages,
            params: error.data.params ?? {},
          };

          this.basketSyncError = null;

          // Only the promotion the request carried is dropped and retried
          if (this.promotionIsStillApplied(sentPromotion)) {
            const droppedPromotion = (this.basket as Basket).data.promotion;

            delete (this.basket as Basket).data.promotion;

            const synced = await this.syncBasket(true);

            // A sync the retry could not land leaves the basket as it was
            if (!synced) {
              (this.basket as Basket).data.promotion = droppedPromotion;

              this.promotionRefusal = null;
            }

            return synced;
          }

          return true;
        }

        if (error.statusCode === 422) {
          const messages = error.data?.errors?.basket;

          this.basketSyncError = Array.isArray(messages)
            ? messages.join(', ')
            : (messages ??
              error.data?.message ??
              'The basket could not be updated.');

          return false;
        }

        this.basketSyncError =
          'Something went wrong while updating the basket. Please try again. If the problem persists, please contact support.';

        Sentry.captureException(error);

        return false;
      }
    },

    /**
     * Whether the error is the API refusing the basket's promotion. Only the
     * two refusals below are keyed on the promotion; any other promotion-keyed
     * 422 is left to the generic handling.
     */
    promotionWasRefused(error: any): boolean {
      if (error.statusCode !== 422 || !error.data?.errors?.promotion) {
        return false;
      }

      return (
        error.data.code === 'promotion.no_free_unit' ||
        error.data.code === 'promotion.no_eligible_items'
      );
    },

    /**
     * Whether the basket still carries the promotion the given request sent. A
     * promotion applied while the request was in flight owns the basket
     * instead.
     */
    promotionIsStillApplied(sent: BasketDiscount | undefined): boolean {
      const current = (this.basket as Basket).data.promotion;

      if (sent === undefined || current === undefined) {
        return false;
      }

      return current.id === sent.id && current.code === sent.code;
    },

    /**
     * Set the server-computed display prices on the basket's products and
     * update the checkout basket.
     */
    applyHydratedBasket(hydrated: Basket): void {
      const { products } = (this.basket as Basket).data;

      hydrated.data.products.forEach((line: BasketItem, index: number) => {
        const product = products[index];

        if (!product) {
          return;
        }

        // Set the product's display price from its and its addons' list prices
        product.display_price =
          (line.list_price ?? 0) +
          (line.addons ?? []).reduce(
            (total: number, addon: BasketItemAddon): number =>
              total + (addon.list_price ?? 0),
            0,
          );
      });

      this.checkoutBasket = {
        ...(this.basket as Basket),
        ...hydrated,
      };
    },

    async setBasketDiscount(discount: {
      id: string;
      eni: string;
      code?: string;
    }): Promise<void> {
      this.summaryPageProcessingDiscount = true;

      this.codeError = null;

      // The terminal's basket is updated by reference
      const basket: Basket = this.basket as Basket;

      // And carries the promotion the validate is sent with
      basket.data.promotion = {
        id: discount.id,
        eni: discount.eni,
      } as BasketDiscount;

      // If the discount has a code set
      if (discount.code) {
        basket.data.promotion.code = discount.code;
      }

      try {
        // Update the basket
        await this.validateBasket();
      } finally {
        this.summaryPageProcessingDiscount = false;
      }
    },

    async removeBasketDiscount(): Promise<void> {
      this.summaryPageProcessingDiscount = true;

      // The terminal's basket is updated by reference
      const basket: Basket = this.basket as Basket;

      // And loses the promotion the validate is sent without
      delete basket.data.promotion;

      // A removed promotion leaves no refusal to answer
      this.promotionRefusal = null;

      try {
        // Update the basket
        await this.validateBasket();
      } finally {
        this.summaryPageProcessingDiscount = false;
      }
    },

    async validateBasket(stockOverride = false): Promise<void> {
      // Use a copy of the terminal's basket
      let basket = JSON.parse(JSON.stringify(this.basket as Basket));

      const body: Basket & { stock_override?: boolean } = this.sanitizeBasket(
        basket as Basket,
      );

      if (stockOverride) {
        body.stock_override = true;
      }

      // The promotion the request carries owns any refusal it comes back with
      const sentPromotion = (basket as Basket).data.promotion;

      try {
        // Update the basket
        const response = await apiPost('/terminal/basket/validate', body);

        // Apply staff discount
        basket = await this.applyStaffDiscountToBasket({
          ...basket,
          ...response.basket,
        });

        // Update the local basket with the provided basket
        this.checkoutBasket = {
          ...this.basket,
          ...basket,
        };

        this.basketStockError = null;

        // An applied promotion replaces the refusal a previous apply left
        if (sentPromotion !== undefined) {
          this.promotionRefusal = null;
        }
      } catch (error: any) {
        // A refused promotion is surfaced as state, never thrown
        if (this.promotionWasRefused(error)) {
          const messages = error.data.errors.promotion;

          // The refusal carries the pairing data its message is built from
          this.promotionRefusal = {
            message: Array.isArray(messages) ? messages.join(', ') : messages,
            params: error.data.params ?? {},
          };

          // Only the promotion the request carried is dropped and re-validated
          if (this.promotionIsStillApplied(sentPromotion)) {
            delete (this.basket as Basket).data.promotion;
            delete basket.data.promotion;

            try {
              await this.validateBasket(stockOverride);
            } catch {
              // A failed retry hides the totals as any unvalidated basket does
              this.basketStockError =
                'Something went wrong while updating the basket. Please try again. If the problem persists, please contact support.';

              this.checkoutBasket = { ...(this.basket as Basket) };
            }

            return;
          }

          // Fall back to the unvalidated basket so the summary page can render
          this.checkoutBasket = { ...(this.basket as Basket) };

          return;
        }

        if (error.statusCode === 422 && error.data?.errors?.basket) {
          const messages = error.data.errors.basket;
          this.basketStockError = Array.isArray(messages)
            ? messages.join(', ')
            : messages;

          // Fall back to the unvalidated basket so the summary page can still
          // render. The basket has no id until validate succeeds, so callers
          // that need it (fetchBasketDiscounts, completeBasket) must gate on
          // basketStockError === null.
          this.checkoutBasket = { ...(this.basket as Basket) };

          return;
        }

        throw error;
      }
    },

    async validateBasketDiscountCode(code: string): Promise<void> {
      this.summaryPageProcessingDiscount = true;

      this.codeError = null;

      try {
        // Validate discount code
        const response = await apiPost('/terminal/basket/promotion/code', {
          code,
        });

        // Set basket discount
        await this.setBasketDiscount({
          id: response.data.id,
          eni: response.data.eni,
          code,
        });
      } catch (error: any) {
        // If the code is not found
        if (error.statusCode === 404) {
          this.codeError = 'Invalid code';

          return;
        }

        // If the code or basket is invalid
        if (error.statusCode === 422) {
          this.codeError = error.data?.message ?? 'Invalid code';

          return;
        }

        throw error;
      } finally {
        this.summaryPageProcessingDiscount = false;
      }
    },

    async fetchBasketDiscounts(): Promise<void> {
      // Fetch basket available discounts
      const response = await apiPost('/terminal/basket/check-promotions', {
        basket_id: (this.checkoutBasket as Basket).id,
      });

      // Set the fetched discounts, keeping an offer that only needs a pair
      this.discounts = response.data.filter(
        (discount: BasketDiscount) =>
          discount.is_valid || discount.unpaired_units !== undefined,
      );
    },

    sanitizeBasket(
      basket: Basket,
      includingStaffDiscount: boolean = true,
    ): Basket {
      // Remove basket generated data
      delete basket.data.display_price;
      delete basket.data.list_price;
      delete basket.data.discounts;
      delete basket.data.tax;
      delete basket.data.cost;

      // For each product
      for (let i: number = 0; i < basket.data.products.length; i++) {
        const product: BasketItem = basket.data.products[i] as BasketItem;

        // Remove basket generated data
        delete product.display_price;
        delete product.monetary_price;
        delete product.list_price;
        delete product.discounts;
        delete product.cost;
        delete product.variant;

        delete product.shipping?.list_price;
        delete product.shipping?.discounts;
        delete product.shipping?.cost;

        if (includingStaffDiscount) {
          delete product.staff_discount;
          delete product.shipping?.staff_discount;
        }

        // For each addon
        for (let ii: number = 0; ii < product.addons.length; ii++) {
          const addon: BasketItemAddon = product.addons[ii] as BasketItemAddon;

          // Remove basket generated data
          delete addon.list_price;
          delete addon.discounts;
          delete addon.cost;

          if (includingStaffDiscount) {
            delete addon.staff_discount;
          }
        }
      }

      return basket;
    },

    async applyStaffDiscountToBasket(basket: Basket): Promise<Basket> {
      const basketData = basket.data;
      let discount: number = this.staffDiscountAmount as number;

      // Only set cost_centre_id if staff discount is being applied
      if (
        (this.staffDiscountAmount ?? 0) > 0 &&
        this.staffDiscountCostCentreId
      ) {
        basket.data.cost_centre_id = this.staffDiscountCostCentreId as string;
      } else {
        // Ensure field is not sent when not applicable
        delete basket.data.cost_centre_id;
      }

      // Only set staff_discount_reason if staff discount is being applied
      if (
        (this.staffDiscountAmount ?? 0) > 0 &&
        this.staffDiscountReason?.trim()
      ) {
        basket.data.staff_discount_reason = this.staffDiscountReason.trim();
      } else {
        // Ensure field is not sent when not applicable
        delete basket.data.staff_discount_reason;
      }

      // If discount is not set or is 0
      if (this.staffDiscountAmount === null || this.staffDiscountAmount === 0) {
        // Do nothing
        return basket;
      }

      // Get the list price of the basket
      let runningTotal: number = basketData.list_price as number;

      // If the basket has a promotion discount
      if (basketData.discounts && basketData.discounts.promotion_discount) {
        // Reduce the basket list price by the discount
        runningTotal -= basketData.discounts.promotion_discount;
      }

      // If the discount amount is more than the basket
      discount = Math.min(discount, runningTotal);

      this.staffDiscountAmount = discount;

      // Amount of staff discount applied
      let appliedDiscount: number = 0;

      // For each product
      for (let i: number = 0; i < basketData.products.length; i++) {
        const product: BasketItem = basketData.products[i];

        // Get the product's list price
        let productRunningTotal: number = product.list_price as number;

        // If the product has a promotion discount
        if (product.discounts && product.discounts.promotion_discount) {
          // Reduce the product's list price by the discount
          productRunningTotal -= product.discounts.promotion_discount;
        }

        // Calculate the proportion of the baskets running total that this product makes
        // up
        const productProportion = productRunningTotal / runningTotal;

        // Assign that proportion of the staff discount to the product
        let productDiscountAmount = Math.floor(productProportion * discount);

        // Ensure the discount amount does not exceed the product's total
        // value
        productDiscountAmount = Math.min(
          productDiscountAmount,
          productRunningTotal,
        );

        // Set the product's staff discount amount
        product.staff_discount = { discount_value: productDiscountAmount };

        // Update applied discount amount
        appliedDiscount += productDiscountAmount;

        // Get the product's shipping list price
        let productShippingRunningTotal: number = product.shipping
          ?.list_price as number;

        // If the product's shipping has a promotion discount
        if (
          product.shipping?.discounts &&
          product.shipping?.discounts.promotion_discount
        ) {
          // Reduce the product's shipping list price by the discount
          productShippingRunningTotal -=
            product.shipping?.discounts.promotion_discount;
        }

        // Calculate the proportion of the basket's running total that this product's shipping makes
        // up
        const productShippingProportion =
          productShippingRunningTotal / runningTotal;

        // Assign that proportion of the staff discount to the product's shipping
        let productShippingDiscountAmount = Math.floor(
          productShippingProportion * discount,
        );

        // Ensure the discount amount does not exceed the product's shipping's total
        // value
        productShippingDiscountAmount = Math.min(
          productShippingDiscountAmount,
          productShippingRunningTotal,
        );

        // Set the product's shipping's staff discount amount
        // @ts-ignore
        product.shipping.staff_discount = {
          discount_value: productShippingDiscountAmount,
        };

        // Update applied discount amount
        appliedDiscount += productShippingDiscountAmount;

        // For each addon
        for (let i: number = 0; i < product.addons.length; i++) {
          const addon: BasketItemAddon = product.addons[i];

          // Get the addon's list price
          let addonRunningTotal: number = addon.list_price as number;

          // If the addon has a promotion discount
          if (addon.discounts && addon.discounts.promotion_discount) {
            // Reduce the addon's list price by the discount
            addonRunningTotal -= addon.discounts.promotion_discount;
          }

          // Calculate the proportion of the baskets running total that this addon makes
          // up
          const addonProportion: number = addonRunningTotal / runningTotal;

          // Assign that proportion of the staff discount to the addon
          let addonDiscountAmount: number = Math.floor(
            addonProportion * discount,
          );

          // Ensure the discount amount does not exceed the addon's total
          // value
          addonDiscountAmount = Math.min(
            addonDiscountAmount,
            addonRunningTotal,
          );

          // Set the addon's staff discount amount
          addon.staff_discount = { discount_value: addonDiscountAmount };

          // Update applied discount amount
          appliedDiscount += addonDiscountAmount;
        }
      }

      if (appliedDiscount !== discount) {
        let missing = discount - appliedDiscount;
        let limit = 5000; // Limit to prevent infinite loop

        while (true) {
          // If the missing amount is 0, stop here
          if (limit <= 0) {
            notifyError(
              'Unable to apply staff discount',
              'Something went wrong while applying the staff discount. Please try again. If the problem persists, please contact support.',
            );
            Sentry.captureMessage('Something went wrong', 'warning');
            break;
          } else {
            limit--;
          }

          // For each product
          for (let i: number = 0; i < basketData.products.length; i++) {
            const product: BasketItem = basketData.products[i];

            // Get the product's list price
            let productRunningTotal: number = product.list_price as number;

            // If the product has a promotion discount
            if (product.discounts && product.discounts.promotion_discount) {
              // Reduce the product's list price by the discount
              productRunningTotal -= product.discounts.promotion_discount;
            }

            // If the product has a staff discount
            if (product.staff_discount?.discount_value) {
              // Reduce the product's list price by the discount
              productRunningTotal -= product.staff_discount.discount_value;
            }

            // If the product total is not 0
            if (productRunningTotal > 0) {
              // @ts-ignore Increase the product staff discount by one unit
              product.staff_discount.discount_value++;

              // Reduce the missing amount by one unit
              missing--;
            }

            if (missing === 0) {
              // Stop here
              break;
            }

            // Get the product's shipping list price
            let productShippingRunningTotal: number = product.shipping
              ?.list_price as number;

            // @ts-ignore If the product shipping has a staff discount
            if (product.shipping.staff_discount?.discount_value) {
              // @ts-ignore Reduce the product's shipping list price by the discount
              productShippingRunningTotal -=
                // @ts-ignore
                product.shipping.staff_discount.discount_value;
            }

            // If the product shipping total is not 0
            if (productShippingRunningTotal > 0) {
              // @ts-ignore Increase the product shipping staff discount by one unit
              product.shipping.staff_discount.discount_value++;

              // Reduce the missing amount by one unit
              missing--;
            }

            if (missing === 0) {
              // Stop here
              break;
            }

            // For each addon
            for (let i: number = 0; i < product.addons.length; i++) {
              const addon: BasketItemAddon = product.addons[i];

              // Get the addon's list price
              let addonRunningTotal: number = addon.list_price as number;

              // If the addon has a promotion discount
              if (addon.discounts && addon.discounts.promotion_discount) {
                // Reduce the addon's list price by the discount
                addonRunningTotal -= addon.discounts.promotion_discount;
              }

              // @ts-ignore If the addon has a staff discount
              if (addon.staff_discount.discount_value) {
                // @ts-ignore Reduce the addon's list price by the discount
                addonRunningTotal -= addon.staff_discount.discount_value;
              }

              // @ts-ignore If the addon total is not 0
              if (addonRunningTotal > 0) {
                // @ts-ignore Increase the addon staff discount by one unit
                addon.staff_discount.discount_value++;

                // Reduce the missing amount by one unit
                missing--;
              }

              if (missing === 0) {
                // Stop here
                break;
              }
            }

            if (missing === 0) {
              // Stop here
              break;
            }
          }

          if (missing === 0) {
            // Stop here
            break;
          }
        }
      }

      // Update the basket
      const response = await apiPost(
        '/terminal/basket/validate',
        this.sanitizeBasket(basket, false),
      );

      return response.basket as Basket;
    },

    async completeBasket(
      type: 'test' | 'live',
      stockOverride = false,
    ): Promise<void> {
      // Use a copy of the terminal's basket
      const basket = JSON.parse(JSON.stringify(this.checkoutBasket as Basket));

      const validateBody: Basket & { stock_override?: boolean } =
        this.sanitizeBasket(basket, false);

      if (stockOverride) {
        validateBody.stock_override = true;
      }

      // Validate the basket
      let response = await apiPost('/terminal/basket/validate', validateBody);

      const data: {
        basket_id: string;
        payment_reference?: string;
        is_test?: boolean;
        buyer_email?: string;
        buyer_name?: string;
        buyer_address?: BuyerAddress;
        stock_override?: boolean;
      } = {
        basket_id: response.basket.id,
      };

      // If a payment reference is set
      if (this.paymentReference !== '') {
        // Include the payment reference when creating the order
        data.payment_reference = this.paymentReference;
      }

      // If the basket type is test
      if (type === 'test') {
        // Send a test flag in the request
        data.is_test = true;
      }

      // Include any captured buyer details (all optional; skip empties to
      // match the API's "skip empty fields" persistence behaviour)
      if (this.buyer.email !== '') {
        data.buyer_email = this.buyer.email;
      }

      if (this.buyer.name !== '') {
        data.buyer_name = this.buyer.name;
      }

      if (Object.values(this.buyer.address).some((field) => field !== '')) {
        data.buyer_address = this.buyer.address;
      }

      // If stock override is set
      if (stockOverride) {
        // Include the stock override flag when creating the order
        data.stock_override = true;
      }

      // Validate the basket
      response = await apiPost('/terminal/basket/order', data);

      // Set the terminal's order
      this.order = response.data as Order;
    },

    /**
     * Send a card payment link to the customer for the given basket. Creates a
     * fresh payment-link transaction each time (a resend leaves the previous
     * attempt to expire). Returns the transaction so the caller can route.
     */
    async sendPaymentLink(
      basketId: string,
      gateway: string,
      email: string,
      type: 'test' | 'live',
    ): Promise<Transaction> {
      // The hosted checkout redirects the customer back to the store's
      // storefront once they have paid (or to the basket if they cancel).
      // Mirrors the storefront's own success/cancel paths. default_domain is
      // the store's canonical public host — its primary custom domain if one
      // is set, otherwise the {subdomain}.{root} default — so links resolve to
      // the customer-facing site regardless of how the store is hosted.
      const host = (this.salesChannel as SalesChannel | null)?.default_domain;

      const body: {
        email: string;
        success_endpoint: string;
        cancel_url: string;
        is_test?: boolean;
      } = {
        email,
        success_endpoint: `https://${host}/payment/success`,
        cancel_url: `https://${host}/checkout`,
      };

      // Mirror completeBasket: flag test-mode sales so the link is created as a
      // test transaction (test gateway keys), never a real charge. Omitted for
      // live, where the API falls back to the store's own mode.
      if (type === 'test') {
        body.is_test = true;
      }

      const response = await apiPost(
        `/terminal/basket/${basketId}/transaction/${gateway}/send-payment-link`,
        body,
      );

      // The send endpoint returns only { id, url }, so keep the staff-entered
      // email on the transaction for the waiting page to show immediately. The
      // first poll replaces this with the canonical payment_link_email.
      this.paymentLinkTransaction = {
        ...(response.data as Transaction),
        payment_link_email: email,
      } as Transaction;

      return this.paymentLinkTransaction;
    },

    /**
     * Re-fetch the current payment-link transaction's status. Used by the
     * waiting page polling loop to detect completion.
     */
    async refreshPaymentLinkStatus(): Promise<void> {
      const transaction = this.paymentLinkTransaction;

      if (!transaction) {
        return;
      }

      const { data } = await apiGet(`/terminal/transactions/${transaction.id}`);

      // If a new link was sent while this request was in flight, the active
      // transaction has changed — discard this stale result so polling stays
      // locked onto the newest transaction rather than reverting to the one
      // it superseded (which the customer will never pay).
      if (this.paymentLinkTransaction?.id !== transaction.id) {
        return;
      }

      this.paymentLinkTransaction = data as Transaction;

      // A non-null completed_at signals the customer has paid; hand the related
      // order to the complete screen as soon as it lands.
      if (data?.completed_at && data?.order) {
        this.order = data.order as Order;
      }
    },

    /**
     * Redeem redeemables.
     */
    async redeem(): Promise<boolean> {
      try {
        await apiPost('/terminal/redeemables/redeem', {
          redeemables: (this.validRedeemables as Redeemable[]).map(
            (
              redeemable: Redeemable,
            ): {
              id: string;
              value?: number;
            } => {
              const redeemableData: {
                id: string;
                value?: number;
              } = { id: redeemable.id };

              if (redeemable.type === 'monetary') {
                redeemableData.value = redeemable.redemption_monetary_value;
              }

              return redeemableData;
            },
          ),
        });

        return true;
      } catch (error: any) {
        // If the request returns 403
        if (error.statusCode === 403) {
          // Return false to indicate the redemption failed
          return false;
        }

        throw error;
      }
    },

    /**
     * Retrieve a redeemable.
     */
    async retrieveRedeemable(redeemableId: string): Promise<void> {
      // Retrieve the redeemable
      const { data } = await apiGet(`/terminal/redeemables/${redeemableId}`);

      if ((data.type as string) === 'addon') {
        data.type = 'enhancement';
      }

      // Set the redeemable in the Pinia store
      this.redeemables = [data];
    },

    /**
     * Retrieve a sales channel.
     */
    async retrieveSalesChannel(salesChannelId: string): Promise<void> {
      // Retrieve the sales channel
      const { data } = await apiGet(`terminal/stores/${salesChannelId}`);

      // Set the sales channel in the Pinia store
      this.salesChannel = data;
    },

    /**
     * Retrieve a product.
     */
    async retrieveProduct(productId: string): Promise<void> {
      // Retrieve the product
      const { data } = await apiGet(`terminal/products/${productId}`);

      // Extract the product's relations that have alternate namings to the API
      const { stores, addons } = data;

      // Delete the product's API named relations from the response data
      delete data.stores;
      delete data.addons;

      // If the terminals product does not have an index
      if (this.product?.index === undefined) {
        // Initialise the basket product
        this.initialiseBasketProduct(data);
      } else {
        // Otherwise, update the terminals product's product data
        this.product = {
          ...data,
          index: this.product.index,
          basketData: this.product.basketData,
        } as BasketProduct;
      }

      // Set extracted product relations to the product
      (this.product as BasketProduct).sales_channels = stores;
      (this.product as BasketProduct).enhancements = addons;
      (this.product as BasketProduct).categories = (
        this.product as BasketProduct
      ).categories.map((category: Category) => ({
        ...category,
        // @ts-ignore since all returned API data has sales channel set as store
        salesChannel: category.store,
      }));
    },

    /**
     * Search products.
     */
    async searchProducts(salesChannelId: string): Promise<void> {
      const query = { ...useRoute().query };

      if (query.search === undefined) {
        query.search = '';
      }

      // Limit products to products attached to the sales channel
      query['filter[stores.id]'] = salesChannelId;

      // And products with active status
      query['filter[is_active]'] = 'true';

      const { data: products } = await apiGet('/terminal/search/products', {
        query,
      });

      this.products = products;
    },

    /**
     * Search redeem locations.
     */
    async searchRedeemLocations(query: string): Promise<void> {
      const { data: redeemLocations } = await apiGet(
        'terminal/search/redeem-locations',
        { query: { search: query } },
      );

      this.redeemLocations = redeemLocations;
    },
  },
});
